import _extends from '@babel/runtime/helpers/extends';
import React, { useCallback, useMemo, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { p as props, d as defaults } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, p as paddingPropTypes, m as marginPropTypes, b as addMarginClasses } from './spacing-0f39ba4a.js';
import { T as Token } from './Token-352a77f8.js';
import { D as Dropdown } from './Dropdown-3c030034.js';
import IconGear from './IconGear.js';
import { L as List } from './List-b06fb467.js';
import { B as Button } from './Button-e85764bc.js';
import IconMessageDots from './IconMessageDots.js';
import { A as Avatar } from './Avatar-548d36a9.js';
import { T as Typography } from './Typography-98688343.js';
import { R as Responsive } from './Responsive-ab9efc3e.js';
import { R as Radio } from './Radio-89e45f54.js';
import { F as FormLabel } from './FormLabel-6bc29108.js';
import { N as Notifications } from './Notifications-3d7f02ba.js';
import IconBell from './IconBell.js';
import { B as Box } from './Box-a46c799c.js';
import useDropdownState from './useDropdownState.js';
import useModalPortal from './useModalPortal.js';
import { M as Modal } from './Modal-506edefb.js';
import useModalState from './useModalState.js';
import IconWsj from './IconWsj.js';
import IconMarketwatch from './IconMarketwatch.js';
import IconBarrons from './IconBarrons.js';
import IconFnlondon from './IconFnlondon.js';
import IconPen from './IconPen.js';
import IconMansionglobal from './IconMansionglobal.js';
import IconDowjones from './IconDowjones.js';
import IconThetimes from './IconThetimes.js';
import IconWsjopinion from './IconWsjopinion.js';
import IconWsjChina from './IconWsjChina.js';
import IconWsjJapan from './IconWsjJapan.js';
import IconIbd from './IconIbd.js';
import IconWsjcommerce from './IconWsjcommerce.js';
import { I as IconBuyside } from './IconBuyside-f01d75d2.js';
import { L as LogoFallback } from './LogoFallback-4ce82dbd.js';
import './IconCross.js';
import './SvgIcon-11e6ad98.js';
import './Icon.js';
import './icons-3c94a1f0.js';
import './zIndex-60238fbe.js';
import './_variables.es6-7ae4f1ec.js';
import './breakpoints-9c323f1e.js';
import './_commonjsHelpers-615a4363.js';
import './Group-0add3181.js';
import './border-67f2c40d.js';
import './Illustration-a7ce5d51.js';
import './Wrapper-2eba1576.js';
import 'react-dom';
import './Grid-76f9b68a.js';
import './ConditionalFocusTrap-186900b2.js';
import './PageWrapper-d8bd64d8.js';
import './body-5de40423.js';

/**
Component to render app name and environment
*/
function HeaderBarBranding({
  className,
  componentEl: Component,
  children,
  env,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar__name st_headerbar__item'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children, env && /*#__PURE__*/React.createElement(Token, {
    caps: true,
    className: "st_headerbar__env",
    margin: {
      left: 'xs'
    },
    color: "blurple"
  }, env));
}
HeaderBarBranding.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** env of app (for token) */
  env: PropTypes.string
};
HeaderBarBranding.defaultProps = {
  className: null,
  componentEl: 'div',
  env: null
};
HeaderBarBranding.displayName = 'HeaderBar.Branding';

/**
Single item for settings dropdown
*/
function SettingsItem({
  className,
  children,
  padding,
  margin,
  ...otherProps
}) {
  let cssClasses = ['st_headersettings__item'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(List.Item, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
SettingsItem.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for item */
  padding: paddingPropTypes,
  /** margin for item */
  margin: marginPropTypes
};
SettingsItem.defaultProps = {
  className: null,
  margin: {
    vertical: 'xs',
    horizontal: 'md'
  },
  padding: null
};
SettingsItem.displayName = 'HeaderBar.Settings.Item';

/**
Single clickable item for settings dropdown
*/
function SettingsLink({
  className,
  componentEl,
  icon,
  children,
  ...otherProps
}) {
  const buttonProps = {
    tertiary: true,
    fullWidth: true,
    icon,
    className
  };
  if (componentEl) buttonProps.componentEl = componentEl;
  return /*#__PURE__*/React.createElement(SettingsItem, {
    margin: {
      vertical: 'xs',
      horizontal: 'smd'
    }
  }, /*#__PURE__*/React.createElement(Button, _extends({}, buttonProps, otherProps), children));
}
SettingsLink.propTypes = {
  /** extra CSS classes, if any, for Button component */
  className: classNamePropTypes,
  /** dom element or component to use for item wrapper */
  componentEl: props,
  /** Optional icon. A screentone icon component, e.g. IconStar */
  icon: PropTypes.any
};
SettingsLink.defaultProps = {
  className: null,
  componentEl: null,
  icon: null
};
SettingsLink.displayName = 'HeaderBar.Settings.Link';

/**
Settings dropdown for header bar
*/
function Settings({
  children,
  open,
  className,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar__item', 'st_headerbar__settings'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Dropdown, {
    padding: {
      all: 'none'
    },
    defaultOpen: open,
    trigger: /*#__PURE__*/React.createElement(IconGear, {
      color: "ink"
    }),
    position: "right"
  }, /*#__PURE__*/React.createElement(List, {
    margin: {
      horizontal: 'none',
      vertical: 'smd'
    }
  }, children)));
}
Settings.propTypes = {
  /** whether dropdown is open or closed */
  open: PropTypes.bool,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
Settings.defaultProps = {
  open: false,
  className: null
};
Settings.displayName = 'HeaderBar.Settings';
Settings.Item = SettingsItem;
Settings.Link = SettingsLink;

/**
Settings dropdown for header bar
*/
function Feedback({
  children,
  open,
  className,
  feedback,
  email,
  slackUrl,
  slackChannel,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar__item', 'st_headerbar__feedback'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Dropdown, {
    padding: {
      all: 'none'
    },
    defaultOpen: open,
    trigger: /*#__PURE__*/React.createElement(IconMessageDots, {
      color: "ink"
    }),
    position: "right"
  }, /*#__PURE__*/React.createElement(List, {
    margin: {
      horizontal: 'none',
      vertical: 'smd'
    }
  }, email && /*#__PURE__*/React.createElement(SettingsLink, {
    target: "_blank",
    componentEl: "a",
    href: `mailto:${email}`
  }, "Email Us"), slackUrl && slackChannel && /*#__PURE__*/React.createElement(SettingsLink, {
    target: "_blank",
    componentEl: "a",
    href: slackUrl
  }, "#", slackChannel, " in Slack"), feedback && /*#__PURE__*/React.createElement(SettingsLink, {
    target: "_blank",
    componentEl: "a",
    href: feedback
  }, "Report Bug / Send Feedback"), children)));
}
Feedback.propTypes = {
  /** whether dropdown is open or closed */
  open: PropTypes.bool,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** email address */
  email: PropTypes.string,
  /** Slack channel url */
  slackUrl: PropTypes.string,
  /** Slack channel name */
  slackChannel: PropTypes.string,
  /** link to provide feedback  */
  feedback: PropTypes.string
};
Feedback.defaultProps = {
  open: false,
  className: null,
  email: null,
  slackUrl: null,
  slackChannel: null,
  feedback: null
};
Feedback.displayName = 'HeaderBar.Feedback';
Feedback.Link = SettingsLink;

function UserNav({
  className,
  componentEl: Component,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_usernav'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    type: "checkbox"
  }, otherProps), children);
}
UserNav.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props
};
UserNav.defaultProps = {
  className: null,
  componentEl: 'div'
};
UserNav.displayName = 'HeaderBar.User.Nav';

/**
User dropdown for header bar
*/
function UserDropdown({
  children,
  name,
  avatar,
  defaultOpen
}) {
  return /*#__PURE__*/React.createElement(Dropdown, {
    defaultOpen: defaultOpen,
    padding: {
      all: 'none'
    },
    position: "right"
  }, /*#__PURE__*/React.createElement(Dropdown.Trigger, {
    componentEl: "button",
    type: "button",
    className: "st_usernav__trigger st_focusable"
  }, name && /*#__PURE__*/React.createElement(Responsive, {
    sm: true,
    md: true,
    lg: true
  }, /*#__PURE__*/React.createElement(Typography, {
    margin: {
      right: 'sm'
    },
    size: "md"
  }, name)), /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    size: "lg",
    initials: name && name.charAt(0) || ' '
  })), /*#__PURE__*/React.createElement(Dropdown.Content, null, /*#__PURE__*/React.createElement(UserNav, null, children)));
}
UserDropdown.propTypes = {
  /** whether dropdown is open or closed by default */
  defaultOpen: PropTypes.bool,
  /** user name to display */
  name: PropTypes.string,
  /** avatar of user, if any */
  avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
};
UserDropdown.defaultProps = {
  defaultOpen: false,
  name: null,
  avatar: null
};
UserDropdown.displayName = 'HeaderBar.User.Dropdown';

/**
wrapper for single user navigation item
*/
function UserNavItem({
  className,
  children,
  padding,
  ...otherProps
}) {
  let cssClasses = ['st_usernav__item'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    cssClasses
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
UserNavItem.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for item. Setting this overrides defaults and does not extend them */
  padding: paddingPropTypes
};
UserNavItem.defaultProps = {
  className: null,
  padding: {
    vertical: 'smd',
    horizontal: 'md'
  }
};
UserNavItem.displayName = 'HeaderBar.User.Item';

/**
Sub-component to display avatar and user info in the dropdown
*/
function UserNavUser({
  avatar,
  name,
  email,
  loginSource
}) {
  if (!name) return null;
  return /*#__PURE__*/React.createElement(UserNavItem, null, /*#__PURE__*/React.createElement("div", {
    className: "st_usernav__userbox"
  }, /*#__PURE__*/React.createElement(Avatar, {
    className: "st_usernav__avatar",
    size: "xl",
    avatar: avatar,
    initials: name.charAt(0)
  }), /*#__PURE__*/React.createElement("div", {
    className: "st_usernav__usertext"
  }, /*#__PURE__*/React.createElement(Typography, {
    weight: "bold",
    size: "md",
    truncate: true
  }, name), email && /*#__PURE__*/React.createElement(Typography, {
    size: "sm",
    color: "asphalt",
    truncate: true
  }, email))), loginSource && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    size: "sm",
    color: "asphalt",
    margin: {
      vertical: 'smd'
    }
  }, "You are logged in via ", typeof loginSource === 'string' ? loginSource : /*#__PURE__*/React.createElement(Typography, {
    variant: "link",
    inline: true,
    componentEl: "a",
    href: loginSource.url,
    rel: "noopener noreferrer",
    target: "_blank"
  }, loginSource.text)), loginSource.settingUrl && /*#__PURE__*/React.createElement(Button, {
    secondary: true,
    componentEl: "a",
    href: loginSource.settingUrl,
    rel: "noopener noreferrer",
    target: "_blank",
    fullWidth: true
  }, "Manage your ", loginSource.text, " Account")));
}
UserNavUser.propTypes = {
  /** url to avatar */
  avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  /** name to display */
  name: PropTypes.string,
  /** email address, if any */
  email: PropTypes.string,
  /**
   * login source, if any, This can be a string with the source name or an
   * object with `text` of the login source name, `url` to link to the login
   * source and a `settingUrl` to link to the users settings/account page
   * */
  loginSource: PropTypes.oneOfType([PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    settingUrl: PropTypes.string
  }), PropTypes.string])
};
UserNavUser.defaultProps = {
  avatar: null,
  name: null,
  email: null,
  loginSource: null
};
UserNavUser.displayName = 'HeaderBar.User.User';

/**
wrapper for single clickable user navigation item
*/
function UserNavLink({
  className,
  componentEl,
  icon,
  children,
  ...otherProps
}) {
  const buttonProps = {
    tertiary: true,
    fullWidth: true,
    icon,
    className
  };
  if (componentEl) buttonProps.componentEl = componentEl;
  return /*#__PURE__*/React.createElement(UserNavItem, {
    padding: {
      vertical: 'smd',
      horizontal: 'smd'
    }
  }, /*#__PURE__*/React.createElement(Button, _extends({}, buttonProps, otherProps), children));
}
UserNavLink.propTypes = {
  /** extra CSS classes, if any, for Button component */
  className: classNamePropTypes,
  /** dom element or component to use for Button component */
  componentEl: props,
  /** optional icon. A screentone icon component, e.g. IconStarm */
  icon: PropTypes.any
};
UserNavLink.defaultProps = {
  className: null,
  componentEl: null,
  icon: null
};
UserNavLink.displayName = 'HeaderBar.User.Link';

/**
 * @deprecated use HeaderBar.Products component instead
Sub-component to display list of products
TODO: Remove component with next major release
*/
function UserNavProducts({
  className,
  componentEl: Component,
  children,
  products,
  product,
  onProductSwitch,
  ...otherProps
}) {
  let cssClasses = ['st_usernav__item'];
  cssClasses = addClassNames(className, cssClasses);

  // calls on the onProductSwitch prop when user clicks on a product
  // adding this so we can pass the value of the selected product
  // rather than a click event
  const localProductSwitch = useCallback(e => {
    if (onProductSwitch) {
      if (e.target.checked) onProductSwitch(e.target.value);
    }
  }, [onProductSwitch]);
  return /*#__PURE__*/React.createElement(UserNavItem, null, /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), products.map(p => /*#__PURE__*/React.createElement(FormLabel, {
    label: p.name,
    labelPosition: "right",
    fullWidth: true,
    key: p.value
  }, /*#__PURE__*/React.createElement(Radio, {
    name: "usernav_product",
    id: `usernav_product_${p.value}`,
    onChange: localProductSwitch,
    value: p.value,
    checked: p.value === product
  })))));
}
UserNavProducts.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** products to display */
  products: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string
  })).isRequired,
  /** current product name */
  product: PropTypes.string,
  /** callback when user switches product */
  onProductSwitch: PropTypes.func
};
UserNavProducts.defaultProps = {
  className: null,
  componentEl: 'div',
  onProductSwitch: null,
  product: null
};
UserNavProducts.displayName = 'HeaderBar.User.Products';

/**
Sub-component to display footer of user nav
*/
function UserNavFooter({
  version,
  credit
}) {
  if (!version && !credit) return null;
  return /*#__PURE__*/React.createElement(UserNavItem, {
    className: "st_usernav__footer"
  }, credit && /*#__PURE__*/React.createElement(Typography, {
    size: "sm",
    color: "ink",
    className: "st_usernav__credit"
  }, "Powered by ", credit), version && /*#__PURE__*/React.createElement(Typography, {
    color: "asphalt",
    size: "sm",
    margin: {
      top: 'xs'
    },
    className: "st_usernav__version"
  }, "Version ", version));
}
UserNavFooter.propTypes = {
  /** version of app to display */
  version: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** credit line, e.g. Powered by Dow Jones */
  credit: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};
UserNavFooter.defaultProps = {
  version: null,
  credit: null
};
UserNavFooter.displayName = 'HeaderBar.User.Footer';

/**
User component for header bar
displays either just the name + avatar
or the name + avatar + dropdown
*/
function User({
  children,
  name,
  avatar,
  className,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar__item', 'st_headerbar__user'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), children && /*#__PURE__*/React.createElement(UserDropdown, {
    avatar: avatar,
    name: name
  }, children), !children && name && /*#__PURE__*/React.createElement(Responsive, {
    sm: true,
    md: true,
    lg: true
  }, /*#__PURE__*/React.createElement(Typography, {
    margin: {
      right: 'sm'
    },
    size: "md"
  }, name)), !children && /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    initials: name && name.charAt(0),
    size: "lg"
  }));
}
User.propTypes = {
  /** user name to display */
  name: PropTypes.string,
  /** avatar of user, if any */
  avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
User.defaultProps = {
  name: null,
  avatar: null,
  className: null
};
User.Item = UserNavItem;
User.User = UserNavUser;
User.Link = UserNavLink;
User.Products = UserNavProducts;
User.Footer = UserNavFooter;
User.Dropdown = UserDropdown;
User.Nav = UserNav;
User.displayName = 'HeaderBar.User';

function NotificationsFooter({
  componentEl: Component,
  children,
  className,
  margin,
  onClick,
  closeUI,
  ...otherProps
}) {
  let cssClasses = ['st_notificationsdropdown__footer'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addMarginClasses(margin, cssClasses);

  // need to short-circuit click bc we need to close modal/dropdown on click
  const handleClick = useCallback(e => {
    closeUI();
    if (typeof onClick === 'function') onClick(e);
  }, [onClick, closeUI]);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    onClick: handleClick
  }, otherProps), children || 'See All');
}
NotificationsFooter.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for headerbar, if any */
  margin: marginPropTypes,
  /** callback to close ui on click, either the dropdown or the modal */
  closeUI: PropTypes.func,
  /** click handler, if any */
  onClick: PropTypes.func
};
NotificationsFooter.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  onClick: null,
  closeUI: () => null
};
NotificationsFooter.displayName = 'HeaderBar.Notifications.Footer';

function NotificationsItem({
  componentEl,
  avatar,
  timestamp,
  initials,
  children,
  highlight,
  onClick,
  closeUI,
  ...otherProps
}) {
  // need to short-circuit click bc we need to close modal/dropdown on click
  const handleClick = useCallback(e => {
    closeUI();
    if (typeof onClick === 'function') onClick(e);
  }, [onClick, closeUI]);
  return /*#__PURE__*/React.createElement(Notifications.Item, _extends({
    componentEl: componentEl,
    avatar: avatar,
    timestamp: timestamp,
    initials: initials,
    highlight: highlight,
    onClick: handleClick
  }, otherProps), children);
}
NotificationsItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** url to avatar, if available */
  avatar: PropTypes.string,
  /** timestamp for event */
  timestamp: PropTypes.string,
  /** whether component should look highlighted */
  highlight: PropTypes.bool,
  /** fallback initials for avatar */
  initials: PropTypes.string,
  /** clickHandler, if any */
  onClick: PropTypes.func,
  /** function to close UI, either the dropdown or the modal */
  closeUI: PropTypes.func
};
NotificationsItem.defaultProps = {
  componentEl: defaults,
  avatar: null,
  highlight: false,
  timestamp: null,
  initials: null,
  onClick: () => null,
  closeUI: () => null
};
NotificationsItem.displayName = 'HeaderBar.Notifications.Item';

const DropdownContext = /*#__PURE__*/createContext({});
function DropdownProvider({
  children,
  onToggle
}) {
  const {
    open,
    componentRef,
    openDropdown,
    closeDropdown,
    toggleDropdown
  } = useDropdownState({
    open: false,
    onToggle
  });
  const data = useMemo(() => ({
    open,
    openUI: openDropdown,
    closeUI: closeDropdown,
    toggleUI: toggleDropdown,
    componentRef
  }), [open, openDropdown, closeDropdown, toggleDropdown, componentRef]);
  return /*#__PURE__*/React.createElement(DropdownContext.Provider, {
    value: data
  }, children);
}
DropdownProvider.propTypes = {
  /** what we want Dropdown to call when open/close state changes */
  onToggle: PropTypes.func
};
DropdownProvider.defaultProps = {
  onToggle: null
};

/**
Notifications dropdown for header bar, for larger breakpoints
*/
function NotificationsDropdown({
  emptyMessage,
  title,
  trigger,
  items,
  footer
}) {
  const dropdownContext = useContext(DropdownContext);
  const {
    open,
    componentRef,
    toggleUI,
    closeUI
  } = dropdownContext;
  const itemsWithContext = React.Children.map(items, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI
    });
  });
  const footerWithContext = React.Children.map(footer, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI
    });
  });
  return /*#__PURE__*/React.createElement(Dropdown, {
    open: open,
    onToggle: toggleUI,
    padding: {
      all: 'none'
    },
    trigger: trigger,
    position: "right",
    componentRef: componentRef
  }, /*#__PURE__*/React.createElement(Box.Title, null, title), /*#__PURE__*/React.createElement(Box.Content, {
    padding: {
      all: 'none'
    },
    className: "st_notificationsdropdown"
  }, /*#__PURE__*/React.createElement("div", {
    className: "st_notificationsdropdown__content"
  }, /*#__PURE__*/React.createElement(Notifications, {
    emptyMessage: emptyMessage
  }, itemsWithContext)), footerWithContext));
}
NotificationsDropdown.propTypes = {
  /** message to display when there are no notifications */
  emptyMessage: PropTypes.string,
  /** title for box */
  title: PropTypes.string,
  /** trigger for notifications modal */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** notification items to display */
  items: PropTypes.node,
  /** notifications footer to display */
  footer: PropTypes.node
};
NotificationsDropdown.defaultProps = {
  title: 'Notifications',
  emptyMessage: null,
  items: null,
  footer: null
};
NotificationsDropdown.displayName = 'HeaderBar.Notifications.Dropdown';

const ModalContext = /*#__PURE__*/createContext({});
function ModalProvider({
  children,
  onToggle
}) {
  const {
    open,
    openModal: openUI,
    closeModal: closeUI
  } = useModalState({
    onToggle
  });
  const data = useMemo(() => ({
    open,
    openUI,
    closeUI
  }), [open, openUI, closeUI]);
  return /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: data
  }, children);
}
ModalProvider.propTypes = {
  /** function callback when modal is opened or closed */
  onToggle: PropTypes.func
};
ModalProvider.defaultProps = {
  onToggle: null
};

/**
Notifications modal for header bar, for mobile breakpoints
*/
function NotificationsModal({
  emptyMessage,
  title,
  trigger,
  items,
  footer,
  onToggle
}) {
  const modalContext = useContext(ModalContext);
  const {
    open,
    openUI,
    closeUI
  } = modalContext;
  const {
    renderNode
  } = useModalPortal();
  const handleOpen = useCallback(() => {
    onToggle(true);
    openUI();
  }, [onToggle, openUI]);
  const handleClose = useCallback(() => {
    onToggle(false);
    closeUI();
  }, [onToggle, closeUI]);
  const itemsWithContext = React.Children.map(items, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI: modalContext.closeUI
    });
  });
  const footerWithContext = React.Children.map(footer, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI: modalContext.closeUI
    });
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    tertiary: true,
    onClick: handleOpen
  }, trigger), open && /*#__PURE__*/React.createElement(Modal, {
    fullWidth: true,
    renderNode: renderNode,
    className: "st_notifications_modal",
    showDismiss: false,
    onDismiss: handleClose
  }, /*#__PURE__*/React.createElement(Box.Title, {
    className: "st_notificationsmodalheader"
  }, title, /*#__PURE__*/React.createElement(Modal.Dismiss, {
    onClick: handleClose
  })), /*#__PURE__*/React.createElement(Box.Content, {
    padding: {
      all: 'none'
    },
    className: "st_notificationsmodal__list"
  }, /*#__PURE__*/React.createElement(Notifications, {
    emptyMessage: emptyMessage
  }, itemsWithContext)), footerWithContext));
}
NotificationsModal.propTypes = {
  /** message to display when there are no notifications */
  emptyMessage: PropTypes.string,
  /** title for box */
  title: PropTypes.string,
  /** trigger for notifications modal */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** notification items to display */
  items: PropTypes.node,
  /** notifications footer to display */
  footer: PropTypes.node,
  /** what to do when modal is opened or closed, returns final open state */
  onToggle: PropTypes.func
};
NotificationsModal.defaultProps = {
  title: 'Notifications',
  emptyMessage: null,
  items: null,
  footer: null,
  onToggle: () => null
};
NotificationsModal.displayName = 'HeaderBar.Notifications.Modal';

/**
notifications dropdown or modal for header bar
*/
function HeaderBarNotifications({
  on,
  emptyMessage,
  title,
  children,
  onToggle,
  className,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar__item', 'st_headerbar__notifications'];
  cssClasses = addClassNames(className, cssClasses);
  const trigger = /*#__PURE__*/React.createElement("div", {
    className: `st_header__notificationalert ${on && 'st_header__notificationalert--on'}`
  }, /*#__PURE__*/React.createElement(IconBell, {
    alt: "notifications",
    color: "ink"
  }));
  const items = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    if (child.type.displayName === 'HeaderBar.Notifications.Item') {
      return child;
    }
    return null;
  });
  const footer = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    if (child.type.displayName === 'HeaderBar.Notifications.Footer') {
      return child;
    }
    return null;
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Responsive, {
    xs: true
  }, /*#__PURE__*/React.createElement(ModalProvider, {
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(NotificationsModal, {
    emptyMessage: emptyMessage,
    title: title,
    trigger: trigger,
    items: items,
    footer: footer
  }))), /*#__PURE__*/React.createElement(Responsive, {
    sm: true,
    md: true,
    lg: true
  }, /*#__PURE__*/React.createElement(DropdownProvider, {
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(NotificationsDropdown, {
    emptyMessage: emptyMessage,
    title: title,
    trigger: trigger,
    items: items,
    footer: footer
  }))));
}
HeaderBarNotifications.propTypes = {
  /** is the bell on? */
  on: PropTypes.bool,
  /** message to display when there are no notifications */
  emptyMessage: PropTypes.string,
  /** title for box */
  title: PropTypes.string,
  /** what to do when dropdown/modal is opened or closed, returns final open state */
  onToggle: PropTypes.func,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
HeaderBarNotifications.defaultProps = {
  title: 'Notifications',
  on: false,
  emptyMessage: null,
  onToggle: null,
  className: null
};
HeaderBarNotifications.displayName = 'HeaderBar.Notifications';
HeaderBarNotifications.Item = NotificationsItem;
HeaderBarNotifications.Footer = NotificationsFooter;

var defaultLogos = {
  wsj: {
    image: IconWsj
  },
  marketwatch: {
    image: IconMarketwatch
  },
  barrons: {
    image: IconBarrons
  },
  fnlondon: {
    image: IconFnlondon
  },
  pen: {
    image: IconPen
  },
  mansionglobal: {
    image: IconMansionglobal
  },
  dowjones: {
    image: IconDowjones
  },
  thetimes: {
    image: IconThetimes
  },
  wsjopinion: {
    image: IconWsjopinion
  },
  cwsj: {
    image: IconWsjChina
  },
  jwsj: {
    image: IconWsjJapan
  },
  ibd: {
    image: IconIbd
  },
  wsjcommerce: {
    image: IconWsjcommerce
  },
  buyside: {
    image: IconBuyside
  }
};

function findProduct(products, product) {
  return products.filter(p => p.value === product);
}

/**
Component to render app name and environment
*/
function HeaderBarProduct({
  className,
  componentEl: Component,
  children,
  type,
  products,
  productName,
  productImage: ImageElement,
  onProductSwitch,
  product: productProp,
  ...otherProps
}) {
  // this is here bc we want to deprecate "type" prop in favor of "product" prop
  // but for the short term, we want to support both
  const product = productProp || type;
  let cssClasses = ['st_headerbar__product st_headerbar__item'];
  cssClasses = addClassNames(className, cssClasses);
  let trigger = null;
  if (defaultLogos[product]) {
    const Logo = defaultLogos[product].image;
    trigger = /*#__PURE__*/React.createElement(Logo, {
      size: "lg",
      className: "st_headerbar__productlogo"
    });
  } else if (ImageElement) {
    trigger = /*#__PURE__*/React.createElement(ImageElement, {
      size: "lg",
      className: "st_headerbar__productlogo"
    });
  } else if (product || productName) {
    // fallback when user doesn't supply an image
    // and passes the product name
    trigger = /*#__PURE__*/React.createElement(LogoFallback, {
      text: product || productName,
      maxlen: 1,
      maxlines: 1
    });
  } else {
    // fallback for when user doesn't supply an image
    // and instead passes children to render instead
    trigger = children;
  }

  // calls on the onProductSwitch prop when user clicks on a product
  // adding this so we can pass the value of the selected product
  // rather than a click event
  const localProductSwitch = useCallback(e => {
    if (onProductSwitch) {
      if (e.target.checked) onProductSwitch(e.target.value);
    }
  }, [onProductSwitch]);

  // if fewer than 2 products are available to user
  // don't bother displaying list of possible products
  if (!products || products.length < 1) {
    cssClasses.push('st_headerbar__product--single');
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: cssClasses.join(' ')
    }, otherProps, {
      "aria-label": `Current Product: ${productName || product}`
    }), trigger);
  }
  cssClasses.push('st_headerbar__product--multi');

  // get aria label for current product
  // either explicitly from the productName prop,
  // or from the products array, or from the product prop.
  const currentProduct = findProduct(products, product);
  const currentProductLabel = productName || currentProduct && currentProduct.name || product;
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Dropdown, null, /*#__PURE__*/React.createElement(Dropdown.Trigger, {
    componentEl: "button",
    className: "st_headerbar__ptrigger st_focusable",
    "aria-label": `Current Product: ${currentProductLabel}`
  }, trigger), /*#__PURE__*/React.createElement(Dropdown.Content, {
    className: "st_headerbar__pdropdown",
    position: "none",
    padding: {
      all: 'smd'
    }
  }, products.map(p => /*#__PURE__*/React.createElement(FormLabel, {
    label: p.name,
    labelPosition: "right",
    fullWidth: true,
    key: p.value
  }, /*#__PURE__*/React.createElement(Radio, {
    name: `header_product_${p.value}`,
    id: `header_product_${p.value}`,
    onChange: localProductSwitch,
    value: p.value,
    checked: p.value === product
  }))))));
}
HeaderBarProduct.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** **DEPRECATED**: name of product. Use `product` instead. */
  type: PropTypes.string,
  /**
   * Component that renders the SVG logo, e.g. `IconWsj`, `IconBarrons`, etc
   * Use if the logo you need does not exist in Screentone.
   */
  productImage: PropTypes.any,
  /**
   * nice name of current product, to be used for a11y, e.g. `Dow Jones`, `MarketWatch`, etc.
   * Use if you do not want to use the product name from the `products` array.
   */
  productName: PropTypes.string,
  /**
   * One of the product passed in the `products` prop.
   * */
  product: PropTypes.string,
  /** Array of all products available. Each product should provide the following info:
   * `name`: name of product to display in dropdown, e.g. `MarketWatch`, `Dow Jones`
   * `value`: id of product, e.g. `marketwatch`, `dowjones`
   */
  products: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string
  })),
  /** callback when user switches product */
  onProductSwitch: PropTypes.func
};
HeaderBarProduct.defaultProps = {
  className: null,
  componentEl: 'div',
  type: null,
  product: null,
  products: null,
  productImage: null,
  productName: null,
  onProductSwitch: null
};
HeaderBarProduct.displayName = 'HeaderBar.Product';

/**
Component to render arbitrary links in HeaderBar
*/
function HeaderBarLinksItem({
  className,
  componentEl,
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React.createElement(Button, _extends({
    tertiary: true
  }, otherProps, {
    componentEl: componentEl
  }), children);
}
HeaderBarLinksItem.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props
};
HeaderBarLinksItem.defaultProps = {
  className: null,
  componentEl: 'a'
};
HeaderBarLinksItem.displayName = 'HeaderBar.Links.Item';

/**
Component to render arbitrary links in HeaderBar
*/
function HeaderBarLinks({
  className,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar__links st_headerbar__item'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
HeaderBarLinks.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
HeaderBarLinks.defaultProps = {
  className: null
};
HeaderBarLinks.Item = HeaderBarLinksItem;
HeaderBarLinks.displayName = 'HeaderBar.Links';

/** @deprecated */
function HeaderBar({
  componentEl: Component,
  children,
  className,
  margin,
  sticky,
  ...otherProps
}) {
  let cssClasses = ['st_headerbar'];
  const cssClassesWrapper = [];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addMarginClasses(margin, cssClasses);
  if (sticky) cssClassesWrapper.push('st_sticky-top');
  return /*#__PURE__*/React.createElement(Component, _extends({}, otherProps, {
    className: cssClassesWrapper.join(' ')
  }), /*#__PURE__*/React.createElement("nav", {
    className: cssClasses.join(' '),
    "aria-label": "Main navigation"
  }, children));
}
HeaderBar.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for headerbar, if any */
  margin: marginPropTypes,
  /** whether header should be sticky using position:sticky */
  sticky: PropTypes.bool
};
HeaderBar.defaultProps = {
  componentEl: 'header',
  className: null,
  margin: null,
  sticky: true
};
HeaderBar.Settings = Settings;
HeaderBar.Feedback = Feedback;
HeaderBar.User = User;
HeaderBar.Branding = HeaderBarBranding;
HeaderBar.Notifications = HeaderBarNotifications;
HeaderBar.Product = HeaderBarProduct;
HeaderBar.Links = HeaderBarLinks;

export { HeaderBar as default };
